import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useProducts } from './useProducts';
import { formatDisplayTitle } from './formatProductTitle';
import { categoryHeroTagline } from './categoryHeroText';
import ProductListingToolbar, { filterInStockOnly, sortProducts } from './catalog/ProductListingToolbar';
import CatalogGridSkeleton from './catalog/CatalogGridSkeleton';
import CatalogPagination from './catalog/CatalogPagination';
import ProductCard from './components/ProductCard';
import CollectionSubheader from './components/CollectionSubheader';
import { productGroupLabel, shopCategoryHubPath } from './productPaths';
import { productMatchesGroup } from './catalogGroupUtils';

const GROUP_TAGLINE_FALLBACK = 'Handcrafted with care — artisan made in India.';

/**
 * Product grid for /shop/:categorySlug/:groupKey — all categories (rakhi, lumba, gifts, …).
 * groupKey "all" shows every product in the category; otherwise matches type OR subcategory.
 */
const DynamicCategoryList = () => {
  const { categorySlug, groupKey } = useParams();
  const decodedGroup = groupKey ? decodeURIComponent(groupKey) : '';
  const { allProducts, categories, loading } = useProducts();

  const meta = useMemo(
    () => categories.find((c) => c.slug === categorySlug),
    [categories, categorySlug]
  );

  const categoryProducts = useMemo(
    () => allProducts.filter((p) => p.category === categorySlug),
    [allProducts, categorySlug]
  );

  const categoryLabel = formatDisplayTitle(meta?.name || categorySlug?.replace(/-/g, ' ') || 'Collection');

  const sectionTitle = useMemo(() => {
    if (!decodedGroup || decodedGroup === 'all') {
      return categoryLabel;
    }
    return productGroupLabel(meta, decodedGroup, categorySlug);
  }, [decodedGroup, meta, categorySlug, categoryLabel]);

  const sectionTagline = useMemo(() => {
    if (decodedGroup && decodedGroup !== 'all') {
      return GROUP_TAGLINE_FALLBACK;
    }
    return categoryHeroTagline(meta, '');
  }, [decodedGroup, meta]);

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('default');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const pageSize = 8;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug, decodedGroup]);

  useEffect(() => {
    setPage(1);
  }, [categorySlug, decodedGroup]);

  const productsToShow = useMemo(() => {
    let list =
      decodedGroup && decodedGroup !== 'all'
        ? categoryProducts.filter((p) => productMatchesGroup(p, decodedGroup))
        : categoryProducts;
    list = filterInStockOnly(list, inStockOnly);
    return sortProducts(list, sort);
  }, [decodedGroup, categoryProducts, inStockOnly, sort]);

  const totalPages = Math.ceil(productsToShow.length / pageSize) || 1;
  const paginated = productsToShow.slice((page - 1) * pageSize, page * pageSize);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!decodedGroup) {
    return (
      <div className="product-list-page product-list-main">
        <div className="container py-5 text-center text-muted">Please select a collection</div>
      </div>
    );
  }

  const hubPath = shopCategoryHubPath(categorySlug);
  const isAllGroup = decodedGroup === 'all';

  return (
    <div className="product-list-page product-list-main">
      <div className="product-list-page__content">
        <div className="container store-page__container pt-3 pt-lg-4">
          {!isAllGroup ? (
            <CollectionSubheader
              breadcrumbs={[
                { to: '/shop', label: 'Shop' },
                { to: hubPath, label: categoryLabel },
                { label: sectionTitle },
              ]}
              backTo={hubPath}
              backLabel={`All ${categoryLabel} styles`}
              title={sectionTitle}
              tagline={sectionTagline}
              count={productsToShow.length}
            />
          ) : (
            <CollectionSubheader
              breadcrumbs={[
                { to: '/shop', label: 'Shop' },
                { label: categoryLabel },
              ]}
              title={sectionTitle}
              tagline={sectionTagline}
              count={productsToShow.length}
            />
          )}

          <ProductListingToolbar
            sort={sort}
            onSortChange={setSort}
            inStockOnly={inStockOnly}
            onInStockOnlyChange={setInStockOnly}
            count={productsToShow.length}
            className="mb-3"
          />

          {loading && paginated.length === 0 ? (
            <CatalogGridSkeleton count={4} />
          ) : paginated.length === 0 ? (
            <div className="text-center py-5 collection-subheader__empty">
              <h2 className="h5 text-muted">No products in this collection yet</h2>
              <p className="text-muted mb-3">Please check back soon for new arrivals.</p>
              <Link to={hubPath} className="btn btn-outline-primary btn-sm rounded-pill px-3">
                Browse all {categoryLabel} styles
              </Link>
            </div>
          ) : (
            <div className="product-grid">
              {paginated.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  categorySlug={categorySlug}
                  linkToDetail
                  imageIndex={currentImageIndex[product.id] || 0}
                  onImageIndexChange={(idx) =>
                    setCurrentImageIndex({ ...currentImageIndex, [product.id]: idx })
                  }
                />
              ))}
            </div>
          )}

          {totalPages > 1 ? (
            <CatalogPagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DynamicCategoryList;
